/**
 * Text on a canvas for bubble titles: wrapping, truncation, contrast (the
 * phone's canvas, canvas-renderer.js, with ctx's current font), and the
 * curved titles of the laptop's view, measured the same way for the room the
 * layout keeps them (render.js) and for drawing them (titles.js).
 */

/**
 * Greedy word-wrap under ctx's current font. Returns at most maxLines
 * lines; overflow is ellipsized into the last line, and every line is
 * individually clamped (covers single words wider than maxWidth).
 */
export function wrapLines(ctx, text, maxWidth, maxLines) {
  const words = String(text).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let cur = words[0];
  let overflow = false;
  for (let i = 1; i < words.length; i++) {
    if (overflow) {
      cur += " " + words[i]; // rest folds into the last line, clamped below
      continue;
    }
    const probe = cur + " " + words[i];
    if (ctx.measureText(probe).width <= maxWidth) {
      cur = probe;
    } else if (lines.length === maxLines - 1) {
      overflow = true;
      cur = probe;
    } else {
      lines.push(cur);
      cur = words[i];
    }
  }
  lines.push(cur);
  return lines.map((l, i) =>
    fitText(ctx, i === lines.length - 1 && overflow ? l + "…" : l, maxWidth),
  );
}

/** Truncate with ellipsis to fit maxWidth under ctx's current font. */
export function fitText(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + "…").width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return lo > 0 ? text.slice(0, lo) + "…" : "";
}

/** Simple luminance-based contrast pick, mirroring the DOM's label colors. */
export function contrastColor(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "#fff";
  const v = parseInt(m[1], 16);
  const r = (v >> 16) & 255;
  const g = (v >> 8) & 255;
  const b = v & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#1a1a1a" : "#fff";
}

// A parent's curved title: bold, 0.5 units between letters (the SVG titles'
// style), measured letter by letter from widths at 100 px, so the layout
// and the drawing agree to the letter.
const TITLE_FONT_FAMILY = '"Inter", -apple-system, BlinkMacSystemFont, sans-serif';
const TITLE_LETTER_SPACING = 0.5;
let titleCtx = null;
const titleWidths = new Map();
if (typeof document !== "undefined") {
  // Measured before the web font arrived: measure again.
  document.fonts?.addEventListener?.("loadingdone", () => titleWidths.clear());
}

function titleLetter(ch) {
  let w = titleWidths.get(ch);
  if (w === undefined) {
    titleCtx ??= document.createElement("canvas").getContext("2d");
    if (!titleCtx) return 0;
    titleCtx.font = `700 100px ${TITLE_FONT_FAMILY}`;
    w = titleCtx.measureText(ch).width;
    titleWidths.set(ch, w);
  }
  return w;
}

/** The font of a curved title of this size (px, or the map's units). */
export function titleFont(fontSize) {
  return `700 ${fontSize}px ${TITLE_FONT_FAMILY}`;
}

/**
 * A curved title as displayed: the whole name when it fits maxWidth,
 * otherwise its start and "…"; with each letter's advance at fontSize.
 * @returns {{text: string, letters: string[], advances: number[], width: number}}
 */
export function fitArcTitle(name, fontSize, maxWidth) {
  let letters = Array.from(name || "");
  const advance = (ch) => (titleLetter(ch) * fontSize) / 100 + TITLE_LETTER_SPACING;
  let advances = letters.map(advance);
  let width = advances.reduce((a, b) => a + b, 0);
  if (width > maxWidth) {
    const ell = advance("…");
    let k = 0;
    let used = 0;
    while (k < letters.length && used + advances[k] + ell <= maxWidth) used += advances[k++];
    while (k > 0 && /\s/.test(letters[k - 1])) k--;
    letters = k ? [...letters.slice(0, k), "…"] : [];
    advances = k ? [...advances.slice(0, k), ell] : [];
    width = advances.reduce((a, b) => a + b, 0);
  }
  return { text: letters.join(""), letters, advances, width };
}
